import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Pagination from "@cloudscape-design/components/pagination";
import ProgressBar from "@cloudscape-design/components/progress-bar";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";
import Textarea from "@cloudscape-design/components/textarea";
import TextFilter from "@cloudscape-design/components/text-filter";

// Sportsbook player groups: each group is a Softswiss tag (type Group) the sportsbook back office can
// pick when creating a bonus. Players are added by their sportsbook user ids.

const JOB_POLL_MS = 2000;
const USERS_PER_PAGE = 50;

const request = (Interface, type, method, data = {}) => Interface.request(type, { class: "data", module: "sportsbookTags", method }, data);

const getErrorText = (error) => {
	if (typeof error === "string") return error;
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;

	return "Something went wrong";
};

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);

	return Number.isNaN(date.getTime()) ? String(value) : `${date.toLocaleDateString("ru-RU")} ${date.toLocaleTimeString("ru-RU")}`;
};

const countIds = (text) => new Set(String(text || "").split(/[\s,;]+/).filter(Boolean)).size;

// Polls a players sync job until it finishes; the server keeps the job for an hour.
function useSyncJob(Interface, onFinish) {
	const [job, setJob] = React.useState(null);
	const timer = React.useRef(null);

	const stop = () => {
		if (timer.current) clearTimeout(timer.current);
		timer.current = null;
	};

	const poll = React.useCallback(
		(jobId) => {
			stop();
			request(Interface, "get", "job", { jobId })
				.then((result) => {
					setJob(result);
					if (result.status === "processing") {
						timer.current = setTimeout(() => poll(jobId), JOB_POLL_MS);
					} else if (onFinish) {
						onFinish(result);
					}
				})
				.catch((error) => setJob({ error: getErrorText(error), status: "failed" }));
		},
		[Interface, onFinish],
	);

	React.useEffect(() => stop, []);

	const start = (jobId) => {
		if (!jobId) return;
		setJob({ done: 0, failed: 0, status: "processing", total: 0 });
		poll(jobId);
	};

	return { job, reset: () => (stop(), setJob(null)), start };
}

function SyncJobStatus({ job }) {
	if (!job) return null;

	const total = job.total || 0;
	const percent = total ? Math.round(((job.done || 0) / total) * 100) : 0;

	if (job.status === "failed") return <Alert type="error" header="Sending to the sportsbook stopped">{job.error}</Alert>;

	return (
		<SpaceBetween size="xs">
			<ProgressBar
				value={percent}
				label="Sending players to the sportsbook"
				additionalInfo={`${job.done || 0} of ${total} players${job.failed ? `, ${job.failed} failed` : ""}`}
				status={job.status === "processing" ? "in-progress" : "success"}
				resultText={job.failed ? `Done, ${job.failed} players were not updated` : "All players updated"}
			/>
			{job.status !== "processing" && job.failedUsers?.length ? (
				<Alert type="warning" header="Players the sportsbook did not accept">
					{job.failedUsers.map((item) => (
						<div key={item.userId}>
							{item.userId}: {item.error}
						</div>
					))}
				</Alert>
			) : null}
		</SpaceBetween>
	);
}

function SportsbookTags({ Interface }) {
	const [groups, setGroups] = React.useState({ items: [], loading: true, error: "" });
	const [createModal, setCreateModal] = React.useState({ visible: false, name: "", userIds: "", saving: false, error: "" });
	const [selected, setSelected] = React.useState(null);

	const loadGroups = React.useCallback(() => {
		setGroups((current) => ({ ...current, loading: true, error: "" }));
		request(Interface, "get", "list")
			.then((result) => setGroups({ items: result.items || [], loading: false, error: "" }))
			.catch((error) => setGroups({ items: [], loading: false, error: getErrorText(error) }));
	}, [Interface]);

	React.useEffect(() => {
		loadGroups();
	}, [loadGroups]);

	const createJob = useSyncJob(Interface, loadGroups);

	const createGroup = () => {
		setCreateModal((current) => ({ ...current, saving: true, error: "" }));
		request(Interface, "set", "create", { name: createModal.name, userIds: createModal.userIds })
			.then((result) => {
				setCreateModal((current) => ({ ...current, saving: false, created: result.group }));
				createJob.start(result.jobId);
				loadGroups();
			})
			.catch((error) => setCreateModal((current) => ({ ...current, saving: false, error: getErrorText(error) })));
	};

	const closeCreate = () => {
		createJob.reset();
		setCreateModal({ visible: false, name: "", userIds: "", saving: false, error: "" });
	};

	return (
		<SpaceBetween size="l">
			<Table
				loading={groups.loading}
				loadingText="Loading groups"
				items={groups.items}
				trackBy="id"
				header={
					<Header
						counter={`(${groups.items.length})`}
						description="Groups of sportsbook players. Each group is sent to the sportsbook as a tag that can be chosen when creating a bonus."
						actions={
							<Button variant="primary" onClick={() => setCreateModal((current) => ({ ...current, visible: true }))}>
								Create group
							</Button>
						}
					>
						Sportsbook groups
					</Header>
				}
				columnDefinitions={[
					{ id: "id", header: "Tag ID", cell: (item) => item.id, width: 100 },
					{ id: "name", header: "Name", cell: (item) => <Button variant="inline-link" onClick={() => setSelected(item)}>{item.name}</Button> },
					{ id: "usersCount", header: "Players", cell: (item) => new Intl.NumberFormat("ru-RU").format(item.usersCount) },
					{ id: "syncedAt", header: "Tag sent to sportsbook", cell: (item) => (item.syncedAt ? formatDateTime(item.syncedAt) : <StatusIndicator type="warning">Not sent</StatusIndicator>) },
					{ id: "createdAt", header: "Created", cell: (item) => formatDateTime(item.createdAt) },
				]}
				empty={<Box textAlign="center">{groups.error ? <Alert type="error">{groups.error}</Alert> : "No groups yet"}</Box>}
			/>

			<Modal
				visible={createModal.visible}
				onDismiss={closeCreate}
				header="Create sportsbook group"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={closeCreate}>
								{createModal.created ? "Close" : "Cancel"}
							</Button>
							{!createModal.created && (
								<Button variant="primary" loading={createModal.saving} onClick={createGroup}>
									Create
								</Button>
							)}
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="m">
					{createModal.error ? <Alert type="error">{createModal.error}</Alert> : null}
					<FormField label="Group name" description="Shown in the sportsbook back office">
						<Input value={createModal.name} disabled={Boolean(createModal.created)} onChange={({ detail }) => setCreateModal((current) => ({ ...current, name: detail.value }))} />
					</FormField>
					<FormField label="Sportsbook player IDs" description={`One per line or separated by commas · ${countIds(createModal.userIds)} unique`}>
						<Textarea rows={10} value={createModal.userIds} disabled={Boolean(createModal.created)} onChange={({ detail }) => setCreateModal((current) => ({ ...current, userIds: detail.value }))} placeholder={"30525259\n30525260"} />
					</FormField>
					{createModal.created ? <Alert type="success">Group «{createModal.created.name}» created, tag ID {createModal.created.id}</Alert> : null}
					<SyncJobStatus job={createJob.job} />
				</SpaceBetween>
			</Modal>

			{selected ? <GroupModal Interface={Interface} group={selected} onClose={() => setSelected(null)} onChanged={loadGroups} /> : null}
		</SpaceBetween>
	);
}

function GroupModal({ Interface, group, onClose, onChanged }) {
	const [name, setName] = React.useState(group.name);
	const [idsText, setIdsText] = React.useState("");
	const [busy, setBusy] = React.useState("");
	const [error, setError] = React.useState("");
	const [confirmDelete, setConfirmDelete] = React.useState(false);
	const [users, setUsers] = React.useState({ items: [], loading: true, page: 1, pages: 1, total: 0 });
	const [search, setSearch] = React.useState("");

	const loadUsers = React.useCallback(
		(page = 1, searchValue = search) => {
			setUsers((current) => ({ ...current, loading: true }));
			request(Interface, "get", "users", { id: group.id, limit: USERS_PER_PAGE, page, search: searchValue })
				.then((result) => setUsers({ items: result.items || [], loading: false, page: result.pagination.page, pages: result.pagination.pages, total: result.pagination.total }))
				.catch((requestError) => {
					setUsers((current) => ({ ...current, loading: false }));
					setError(getErrorText(requestError));
				});
		},
		[Interface, group.id, search],
	);

	React.useEffect(() => {
		loadUsers(1);
	}, [group.id]);

	const syncJob = useSyncJob(
		Interface,
		React.useCallback(() => {
			loadUsers(1);
			onChanged();
		}, [loadUsers, onChanged]),
	);

	const run = (action, method, payload, afterSuccess) => {
		setBusy(action);
		setError("");
		request(Interface, "set", method, { id: group.id, ...payload })
			.then((result) => {
				setBusy("");
				if (result?.jobId) syncJob.start(result.jobId);
				if (afterSuccess) afterSuccess(result);
				onChanged();
			})
			.catch((requestError) => {
				setBusy("");
				setError(getErrorText(requestError));
			});
	};

	return (
		<Modal
			visible
			size="large"
			onDismiss={onClose}
			header={`Group «${group.name}» · tag ID ${group.id}`}
			footer={
				<Box float="right">
					<Button onClick={onClose}>Close</Button>
				</Box>
			}
		>
			<SpaceBetween size="l">
				{error ? <Alert type="error">{error}</Alert> : null}
				<SyncJobStatus job={syncJob.job} />

				<Container header={<Header variant="h3">Group</Header>}>
					<SpaceBetween size="s">
						<FormField label="Name">
							<Input value={name} onChange={({ detail }) => setName(detail.value)} />
						</FormField>
						<SpaceBetween direction="horizontal" size="xs">
							<Button loading={busy === "edit"} disabled={!name.trim() || name.trim() === group.name} onClick={() => run("edit", "edit", { name })}>
								Save name
							</Button>
							<Button loading={busy === "resync"} onClick={() => run("resync", "resync", {})}>
								Send again to sportsbook
							</Button>
							{confirmDelete ? (
								<>
									<Button variant="primary" loading={busy === "delete"} onClick={() => run("delete", "delete", {}, onClose)}>
										Yes, delete the group
									</Button>
									<Button variant="link" onClick={() => setConfirmDelete(false)}>
										Keep it
									</Button>
								</>
							) : (
								<Button onClick={() => setConfirmDelete(true)}>Delete group</Button>
							)}
						</SpaceBetween>
						{confirmDelete ? <Box color="text-status-warning">The tag is removed from the sportsbook and from every player of the group.</Box> : null}
					</SpaceBetween>
				</Container>

				<Container header={<Header variant="h3">Add or remove players</Header>}>
					<SpaceBetween size="s">
						<FormField label="Sportsbook player IDs" description={`One per line or separated by commas · ${countIds(idsText)} unique`}>
							<Textarea rows={6} value={idsText} onChange={({ detail }) => setIdsText(detail.value)} />
						</FormField>
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="primary" loading={busy === "add"} disabled={!countIds(idsText)} onClick={() => run("add", "addUsers", { userIds: idsText }, () => setIdsText(""))}>
								Add to group
							</Button>
							<Button loading={busy === "remove"} disabled={!countIds(idsText)} onClick={() => run("remove", "removeUsers", { userIds: idsText }, () => setIdsText(""))}>
								Remove from group
							</Button>
						</SpaceBetween>
					</SpaceBetween>
				</Container>

				<Table
					loading={users.loading}
					loadingText="Loading players"
					items={users.items}
					trackBy="userId"
					header={<Header counter={`(${users.total})`}>Players</Header>}
					filter={
						<TextFilter
							filteringText={search}
							filteringPlaceholder="Find by player ID"
							onChange={({ detail }) => setSearch(detail.filteringText)}
							onDelayedChange={({ detail }) => loadUsers(1, detail.filteringText)}
						/>
					}
					pagination={<Pagination currentPageIndex={users.page} pagesCount={users.pages} onChange={({ detail }) => loadUsers(detail.currentPageIndex)} />}
					columnDefinitions={[
						{ id: "userId", header: "Sportsbook player ID", cell: (item) => item.userId },
						{ id: "addedAt", header: "Added", cell: (item) => formatDateTime(item.addedAt) },
						{
							id: "sync",
							header: "Sportsbook",
							cell: (item) => {
								if (item.syncError) return <StatusIndicator type="error">{item.syncError}</StatusIndicator>;
								if (item.syncedAt) return <StatusIndicator type="success">Updated {formatDateTime(item.syncedAt)}</StatusIndicator>;

								return <StatusIndicator type="pending">Not sent yet</StatusIndicator>;
							},
						},
					]}
					empty={<Box textAlign="center">No players in this group</Box>}
				/>
			</SpaceBetween>
		</Modal>
	);
}

export default SportsbookTags;
